import { useNavigate, useLocation } from 'react-router-dom'
import { User } from '@/lib/api'
import {
  LayoutDashboard,
  Receipt,
  LogOut,
  Sparkles,
  DollarSign,
  BookOpen,
  BarChart3,
  TrendingUp,
  FileText
} from 'lucide-react'

interface DashboardSidebarProps {
  user: User | null
  onLogout: () => void
  onNavigate?: () => void
}

interface SidebarItemProps {
  icon: React.ElementType
  label: string
  active: boolean
  onClick: () => void
}

const SidebarItem = ({ icon: Icon, label, active, onClick }: SidebarItemProps) => (
  <button
    onClick={onClick}
    className={`group flex items-center w-full gap-3 px-4 py-3 rounded-xl transition-all duration-200 ease-out
      ${active
        ? 'bg-cyan-600/20 text-cyan-300 border border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }`}
  >
    <Icon size={20} className={`${active ? 'text-cyan-300' : 'text-slate-400 group-hover:text-cyan-400'}`} />
    <span className="font-medium text-sm tracking-wide">{label}</span>
    {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />}
  </button>
)

export default function DashboardSidebar({ user, onLogout, onNavigate }: DashboardSidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  const handleNavClick = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  return (
    <div className="h-full flex flex-col bg-[#0F1422]/80 backdrop-blur-xl border-r border-slate-800/50">
      {/* Logo Area */}
      <div className="p-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
          <Sparkles size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-white leading-tight">Accounting AI</h1>
          <span className="text-xs text-cyan-400 font-medium tracking-wide">Business Finance</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto custom-scrollbar">
        <div className="text-xs font-semibold text-slate-500 px-4 mb-2 uppercase tracking-wider">Main Menu</div>

        <SidebarItem
          icon={LayoutDashboard}
          label="Dashboard"
          active={pathname === '/dashboard'}
          onClick={() => handleNavClick('/dashboard')}
        />
        <SidebarItem
          icon={FileText}
          label="Document History"
          active={pathname === '/documents'}
          onClick={() => handleNavClick('/documents')}
        />
        <SidebarItem
          icon={DollarSign}
          label="Chart of Accounts"
          active={pathname === '/chart-of-accounts'}
          onClick={() => handleNavClick('/chart-of-accounts')}
        />
        <SidebarItem
          icon={Receipt}
          label="Transactions"
          active={pathname === '/transactions'}
          onClick={() => handleNavClick('/transactions')}
        />
        <SidebarItem
          icon={BookOpen}
          label="General Journal"
          active={pathname === '/general-journal'}
          onClick={() => handleNavClick('/general-journal')}
        />

        <div className="text-xs font-semibold text-slate-500 px-4 mt-6 mb-2 uppercase tracking-wider">Reports</div>

        <SidebarItem
          icon={BarChart3}
          label="Balance Sheet"
          active={pathname === '/reports/balance-sheet'}
          onClick={() => handleNavClick('/reports/balance-sheet')}
        />
        <SidebarItem
          icon={TrendingUp}
          label="Profit & Loss"
          active={pathname === '/reports/profit-and-loss'}
          onClick={() => handleNavClick('/reports/profit-and-loss')}
        />
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-slate-800/50">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-400 p-[2px]">
            <div className="w-full h-full rounded-full bg-[#0F1422] flex items-center justify-center">
              <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-indigo-400">
                {user?.first_name?.charAt(0) || 'U'}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.first_name || 'User'}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={onLogout}
            className="text-slate-400 hover:text-white transition-colors"
            title="Logout"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </div>
  )
}
